import React from 'react';
import { Box, Container, Header, StatusIndicator, Table } from '@amzn/awsui-components-react';
import {
  getReplicationSummary,
  IndexReplicationRow,
  parseIndexReplication,
} from './indexReplication';
import { hasFollowerReplicationActivity } from './utils';

interface IndexReplicationTableProps {
  followerStats: any;
}

const fmtNullable = (n: number | null): string => (n === null ? 'N/A' : String(n));

const indexColumnDefinitions = [
  {
    id: 'indexName',
    header: 'Index',
    cell: (r: IndexReplicationRow) => r.indexName || 'N/A',
  },
  {
    id: 'followerCheckpoint',
    header: 'Follower checkpoint',
    cell: (r: IndexReplicationRow) => fmtNullable(r.followerCheckpoint),
  },
  {
    id: 'leaderCheckpoint',
    header: 'Leader checkpoint',
    cell: (r: IndexReplicationRow) => fmtNullable(r.leaderCheckpoint),
  },
  {
    id: 'checkpointLag',
    header: 'Checkpoint lag',
    cell: (r: IndexReplicationRow) => fmtNullable(r.checkpointLag),
  },
  {
    id: 'operationsWritten',
    header: 'Operations written',
    cell: (r: IndexReplicationRow) => String(r.operationsWritten),
  },
  {
    id: 'failedRequests',
    header: 'Failed requests',
    cell: (r: IndexReplicationRow) =>
      r.failedRequests > 0 ? (
        <StatusIndicator type="error">{r.failedRequests}</StatusIndicator>
      ) : (
        String(r.failedRequests)
      ),
  },
];

const IndexReplicationTable = (props: IndexReplicationTableProps) => {
  const { followerStats } = props;

  const rows = parseIndexReplication(followerStats);
  const summary = getReplicationSummary(followerStats);
  const isFollower = hasFollowerReplicationActivity(followerStats);

  const summaryText = `${summary.syncing} syncing · ${summary.paused} paused · ${summary.failed} failed · ${summary.bootstrapping} bootstrapping`;

  let body: React.ReactNode;
  if (rows.length > 0) {
    body = (
      <Table
        variant="embedded"
        columnDefinitions={indexColumnDefinitions}
        items={rows}
        resizableColumns={false}
        wrapLines={true}
      />
    );
  } else if (isFollower) {
    body = (
      <Box textAlign="center" padding="m" color="text-status-inactive">
        No indices are currently being replicated on this domain.
      </Box>
    );
  } else {
    body = (
      <Box textAlign="center" padding="m" color="text-status-inactive">
        Index-level replication details are only available on follower domains.
      </Box>
    );
  }

  const header = (
    <Header variant="h2" description={isFollower ? summaryText : undefined}>
      Index-Level Replication
    </Header>
  );

  return <Container header={header}>{body}</Container>;
};

export { IndexReplicationTable };
